import React, { FC } from "react"
import { useMutation } from "@apollo/client/react"
import { Modal } from "@bancacfplus/Modal"
import SubmitButton from "@bancacfplus/SubmitButton"
import { useUserPrivacyPolicy } from "./hooks/useUserPrivacyPolicy"
import { ErrorBoundaryEnriched } from "../ErrorBoundaryEnriched"
import { setUserBehavioursMutation } from "../../queries/setUserBehaviours"
import { setUserBehaviours } from "../../queries/__generated-typings__/setUserBehaviours"
import style from "./PrivacyPolicy.module.scss"
import Checkbox from "@bancacfplus/Checkbox"
import { useForm } from "react-hook-form"
import { Docs, getDocsByClientSource } from "../../utils/getDocsByClientSource"

type Form = { transparency_kit: boolean }

export const PrivacyPolicy: FC = () => {
  const { data: userData, error, loading } = useUserPrivacyPolicy()
  const [setUserPrivacyPolicy, { data, loading: loadingMutation, error: errorMutation }] =
    useMutation<setUserBehaviours>(setUserBehavioursMutation)

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<Form>({
    reValidateMode: "onChange"
  })

  if (loading || loadingMutation) {
    return <></>
  }

  if (error || errorMutation) {
    return (
      <Modal close="dark" hash={false} type="cta" opened={true} className={style.modal} id="privacy-policy-error">
        <ErrorBoundaryEnriched error={error || errorMutation} swallow />
      </Modal>
    )
  }

  const onSubmit = () => {
    setUserPrivacyPolicy({
      variables: {
        eventLabel: "privacy_policy_and_terms_conditions",
        eventValue: "true",
        user_id: userData.id
      }
    })
  }

  const transparencyKitRegister = register("transparency_kit", { required: true })
  const docs = getDocsByClientSource()

  const documentList: { key: keyof Docs; label: string }[] = [
    {
      key: "guidaABF" as const,
      label: "ABF in parole semplici"
    },
    {
      key: "centraleRischi",
      label: "Guida sulla centrale dei rischi"
    },
    {
      key: "foglioInformativo",
      label: "Foglio informativo Digital Lending"
    },
    {
      key: "tegmAntiusura",
      label: "Rilevazione Tassi Effettivi Globali Medi ai sensi dell'art.2 della Legge n.108/1996"
    },
    {
      key: "informativaPrivacy",
      label: "Informativa sul trattamento dei dati personali"
    },
    {
      key: "informativaSic",
      label: "Informativa sui sistemi di informazioni creditizie (SIC)"
    },
    {
      key: "facsimile",
      label: "Contratto di finanziamento Digital Lending Fac Simile"
    },
    {
      key: "termsAndConditions",
      label: "Contratto di termini e condizioni"
    }
  ]

  return (
    <>
      {/* User (coming from partners application) not found in user_behaviours table in golgotha, show modal */}
      {userData.askToReadPrivacyPolicy && !data && (
        <Modal close="dark" hash={false} type="cta" opened={true} className={style.modal} id="privacy-policy-modal">
          <h4 className={style.modalTitle}>Documentazione di Trasparenza</h4>
          <form noValidate onSubmit={handleSubmit(onSubmit)}>
            <fieldset name="checks" className={style.modalContent}>
              <legend id="privacy-policy" className={style.screenReaderOnly}>
                Documentazione di Trasparenza
              </legend>
              <Checkbox
                propsRef={transparencyKitRegister.ref as unknown as React.RefObject<HTMLInputElement>}
                input={{ ...transparencyKitRegister }}
                name={transparencyKitRegister.name}
                text={<p>Ho preso visione della documentazione di Trasparenza: </p>}
                error={errors.transparency_kit ? "Questo campo è obbligatorio" : undefined}
                invalid={!!errors.transparency_kit}
                className={style.checkbox}
              />
              <div className={style.documentsWrapper}>
                {documentList.map(document => (
                  <a
                    key={document.key}
                    href={docs[document.key]}
                    rel="noopener noreferrer"
                    target="_blank"
                    download
                    className={style.documentsLinks}
                  >
                    {document.label}
                  </a>
                ))}
              </div>
            </fieldset>

            <SubmitButton type="submit">Avanti</SubmitButton>
          </form>
        </Modal>
      )}
    </>
  )
}
